export interface ValidationError {
  field: string;
  message: string;
}

export class ValuationValidationError extends Error {
  public readonly errors: ValidationError[];

  constructor(errors: ValidationError[]) {
    const summary = errors.map((e) => `${e.field}: ${e.message}`).join("; ");
    super(`Validation failed: ${summary}`);
    this.name = "ValuationValidationError";
    this.errors = errors;
  }
}

export interface MultiplesValidationInput {
  revenueLastTaxReturn: number;
  profitPlusOwnerCompensation: number;
  ownerHoursPerWeek: number;
  yearsOperating: number;
  revenueHistory?: number[];
}

export function validateMultiplesInput(
  input: MultiplesValidationInput,
): void {
  const errors: ValidationError[] = [];

  if (input.revenueLastTaxReturn < 0) {
    errors.push({
      field: "revenueLastTaxReturn",
      message: "Revenue cannot be negative",
    });
  }

  if (input.profitPlusOwnerCompensation < 0) {
    errors.push({
      field: "profitPlusOwnerCompensation",
      message: "SDE (profit + owner compensation) cannot be negative",
    });
  }

  if (
    input.revenueLastTaxReturn > 0 &&
    input.profitPlusOwnerCompensation > input.revenueLastTaxReturn * 1.5
  ) {
    errors.push({
      field: "profitPlusOwnerCompensation",
      message:
        "SDE exceeds 150% of revenue — this is implausible; check the inputs",
    });
  }

  if (input.ownerHoursPerWeek < 0 || input.ownerHoursPerWeek > 168) {
    errors.push({
      field: "ownerHoursPerWeek",
      message: "Owner hours must be between 0 and 168 per week",
    });
  }

  if (input.yearsOperating < 0) {
    errors.push({
      field: "yearsOperating",
      message: "Years operating cannot be negative",
    });
  }

  if (input.revenueHistory) {
    if (input.revenueHistory.length > 10) {
      errors.push({
        field: "revenueHistory",
        message: "Revenue history should contain at most 10 years",
      });
    }
    for (let i = 0; i < input.revenueHistory.length; i++) {
      if (input.revenueHistory[i] < 0) {
        errors.push({
          field: `revenueHistory[${i}]`,
          message: "Historical revenue cannot be negative",
        });
      }
    }
  }

  if (errors.length > 0) {
    throw new ValuationValidationError(errors);
  }
}

export interface DcfValidationInput {
  historicalYearsCount: number;
  projectionYearsCount: number;
  longTermGrowthRate?: number;
  waccOwnerHoursPerWeek: number;
  waccYearsOperating: number;
}

export function validateDcfInput(input: DcfValidationInput): void {
  const errors: ValidationError[] = [];

  if (input.historicalYearsCount < 2) {
    errors.push({
      field: "historicalYears",
      message: "At least 2 years of historical data are required",
    });
  }

  if (input.projectionYearsCount < 1) {
    errors.push({
      field: "projectionAssumptions",
      message: "At least 1 year of projection assumptions is required",
    });
  }

  if (input.projectionYearsCount > 10) {
    errors.push({
      field: "projectionAssumptions",
      message: "Projection horizon should not exceed 10 years",
    });
  }

  if (input.longTermGrowthRate !== undefined) {
    if (input.longTermGrowthRate < -0.05) {
      errors.push({
        field: "longTermGrowthRate",
        message: "Long-term growth rate below -5% is implausible",
      });
    }
    if (input.longTermGrowthRate > 0.10) {
      errors.push({
        field: "longTermGrowthRate",
        message:
          "Long-term growth rate above 10% is unsustainable over time",
      });
    }
  }

  if (
    input.waccOwnerHoursPerWeek < 0 ||
    input.waccOwnerHoursPerWeek > 168
  ) {
    errors.push({
      field: "waccInputs.ownerHoursPerWeek",
      message: "Owner hours must be between 0 and 168 per week",
    });
  }

  if (input.waccYearsOperating < 0) {
    errors.push({
      field: "waccInputs.yearsOperating",
      message: "Years operating cannot be negative",
    });
  }

  if (errors.length > 0) {
    throw new ValuationValidationError(errors);
  }
}
